import {Box, Link} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import type React from 'react'
import type {RefObject} from 'react'
import {LABELS} from '../../constants/labels'
import {SafeHTMLText, type SafeHTMLString} from '@github-ui/safe-html'
import {issueHovercardPath} from '@github-ui/paths'
import type {HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'

import {getIcon} from '../sections/relations-section/ParentIssue'
import type {OptionConfig} from '../OptionConfig'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'

type HeaderParentTitleProps = {
  parentKey?: HeaderParentTitle$key
} & SharedProps

type SharedProps = {
  optionConfig?: OptionConfig
  containerRef?: RefObject<HTMLElement> | undefined
}

/** The internal component that renders the parent title itself after all data fetching has occurred */
export const HeaderParentTitle = ({parentKey, optionConfig, containerRef}: HeaderParentTitleProps) => {
  const data = useFragment(
    graphql`
      fragment HeaderParentTitle on Issue {
        parent {
          number
          repository {
            name
            owner {
              login
            }
          }
          state
          stateReason
          title
          url
        }
      }
    `,
    parentKey,
  )
  const breakpoint = useContainerBreakpoint(containerRef?.current ?? null)

  if (!data) {
    return null
  }
  const {parent} = data

  // The data from this fragment is not _all_ available yet, as it's requested in the secondary query, however,
  // AddSubIssueButtonGroup requests parent.id which is available in the primary query. useFragment, seemingly
  // merges this partial data and we are left with a parent object that is not empty, but also not fully populated.
  // See: https://github.com/github/github/issues/333236
  if (!parent?.repository) {
    return null
  }

  const repo = parent.repository.name
  const owner = parent.repository.owner.login

  const itemIdentifier = {number: parent.number, repo, owner, type: 'Issue'} as const

  const stateIcon = getIcon(parent.state, parent.stateReason, {width: 14, height: 14})
  if (!stateIcon) return null

  const handleClick = (e: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return

    if (optionConfig?.onParentIssueActivate) {
      optionConfig.onParentIssueActivate(e, itemIdentifier)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key !== 'Enter' || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return

    if (optionConfig?.onParentIssueActivate) {
      optionConfig.onParentIssueActivate(e, itemIdentifier)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: 'fg.muted',
        height: 24,
        top: optionConfig?.useViewportQueries
          ? ['-8px', '-8px', '-21px', '-21px']
          : breakpoint(['-8px', '-8px', '-21px', '-21px']),
        position: 'absolute',
        /**
         * NOTE: The queries below are used to determine the width of the parent title. The width is calculated based on
         * the viewport width or the pane width (if in a pinned side panel). These aren't ideal implementations, and
         * we may want to consider porting this to a more robust solution (like container queries) in the future for
         * the padding/margin calculations as well. (https://github.com/github/sub-issues/issues/438)
         */
        // Calculating the *actual* computed width of the panel, which is clamped between the min and max width.  This
        // is not stored as a variable, so we are computing it here.
        '--effective-pane-width': 'clamp(var(--pane-min-width),var(--pane-width),var(--pane-max-width))',
        // If we are inside of the side panel (pinned or not), then --pane-width will be defined and be used. Otherwise,
        // we will fall back to 100% of the parent container width and subtract some pixels to account for padding and such.
        width: 'calc(var(--effective-pane-width, 100%) - 150px)',
        // No matter the viewport size: we always try to show the first 25 characters of the parent issue title. Then, if the
        // viewport size (or pane width if in a pinned side panel in Projects), then we try to take up to 50% of the width.
        minWidth: 'max(var(--effective-pane-width, 100vw)*0.5, 25ch)',
      }}
    >
      {stateIcon}
      <Link
        aria-label={`Parent issue: ${parent.title}`}
        href={parent.url}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-hovercard-url={issueHovercardPath({
          owner,
          repo,
          issueNumber: parent.number,
        })}
        // We will truncate the title text to a single line since it can be very long and we don't want to
        // cause any layout shift if it goes to two lines. 85% of the viewport width was chosen as a value that looks
        // reasonably good on narrow viewports, and scales up well on large viewports
        sx={{
          color: 'fg.muted',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          // Need to set font size and line height explicityle since the parent title is not a part of any primer page header sub components.
          fontSize: 'var(--text-body-size-medium)',
          lineHeight: 'var(--text-body-lineHeight-medium)',
        }}
      >
        <SafeHTMLText html={parent.title as SafeHTMLString} /> <Box as="span">{LABELS.issueNumber(parent.number)}</Box>
      </Link>
    </Box>
  )
}

try{ HeaderParentTitle.displayName ||= 'HeaderParentTitle' } catch {}